import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { getRequestErrorMessage, ServerError } from 'global/helpers/requestError'
import { type ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { downloadDecaDocument, triggerBlobDownload } from 'screens/Deca/Deca.service'

const isDecaUnavailableError = (error: unknown): boolean => {
  if (error instanceof ServerError) {
    return error.response.status === 503
  }
  return false
}

const Deca = (): ReactElement | null => {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()

  const downloadDecaMutation = useMutation({
    mutationFn: downloadDecaDocument,
    onSuccess: (blob) => {
      triggerBlobDownload(blob)
    },
  })

  useEffect(() => {
    if (token) {
      downloadDecaMutation.mutate(token)
    }
  }, [token])

  if (!token) {
    return null
  }

  return (
    <Container maxWidth="xxl">
      {downloadDecaMutation.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.deca.loading')}</Typography>
        </KNLoader>
      )}
      {downloadDecaMutation.isError && (
        <Alert severity="error">
          {isDecaUnavailableError(downloadDecaMutation.error) ? t('screens.cs.deca.unavailable') : getRequestErrorMessage(downloadDecaMutation.error)}
        </Alert>
      )}
      {downloadDecaMutation.isSuccess && (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => triggerBlobDownload(downloadDecaMutation.data)}
            >
              {t('screens.cs.deca.download_again')}
            </Button>
          }
        >
          {t('screens.cs.deca.success')}
        </Alert>
      )}
    </Container>
  )
}

export default Deca
